/**
 * useTimelineFilterLabels Hook
 *
 * Calculates active filter count and generates display labels for active filters.
 * Memoized to prevent unnecessary recalculations.
 */

import { useMemo } from "react";
import type { TimelineFilters } from "../types/timeline-filters";

interface UseTimelineFilterLabelsReturn {
  activeFilterCount: number;
  activeFilterLabels: string[];
}

/**
 * Hook for calculating active filter count and labels
 *
 * @param filters - Current filter values
 * @returns Active filter count and labels
 */
export function useTimelineFilterLabels(
  filters: TimelineFilters
): UseTimelineFilterLabelsReturn {
  // Memoize active filter count calculation
  const activeFilterCount = useMemo(
    () =>
      (filters.type?.length || 0) +
      (filters.status?.length || 0) +
      (filters.direction?.length || 0) +
      (filters.dateFrom ? 1 : 0) +
      (filters.dateTo ? 1 : 0),
    [filters]
  );

  // Get active filter labels for display
  const activeFilterLabels = useMemo(() => {
    const labels = [];
    if (filters.type?.length) {
      labels.push(`Types: ${filters.type.length}`);
    }
    if (filters.status?.length) {
      labels.push(`Status: ${filters.status.length}`);
    }
    if (filters.direction?.length) {
      labels.push(`Direction: ${filters.direction.length}`);
    }
    if (filters.dateFrom || filters.dateTo) {
      labels.push("Date Range");
    }
    return labels;
  }, [filters]);

  return {
    activeFilterCount,
    activeFilterLabels,
  };
}
